/**
 * Tax Engine - Handles all tax-related calculations
 * Separated from UI components for modularity and testability
 */

// GST Tax Slabs (India)
const GST_SLABS = {
  EXEMPT: 0,
  LOW: 5,
  STANDARD: 12,
  HIGH: 18,
  LUXURY: 28,
};

// HSN Code to Tax Rate mapping (simplified)
const HSN_TAX_MAP = {
  // Food items (Chapter 1-21)
  '0': GST_SLABS.EXEMPT,
  '1': GST_SLABS.EXEMPT,
  '2': GST_SLABS.LOW,
  
  // Textiles (Chapter 50-63)
  '5': GST_SLABS.LOW,
  '6': GST_SLABS.STANDARD,
  
  // Electronics (Chapter 84-85)
  '84': GST_SLABS.HIGH,
  '85': GST_SLABS.HIGH,
  
  // Services (SAC codes start with 99)
  '99': GST_SLABS.HIGH,
};

/**
 * Calculate tax amount based on price and tax rate
 * @param {number} price - Base price
 * @param {number} taxRate - Tax rate in percentage
 * @returns {object} Tax calculation breakdown
 */
export const calculateTax = (price, taxRate) => {
  if (!price || price <= 0) {
    return { basePrice: 0, taxAmount: 0, totalPrice: 0, taxRate: 0 };
  }

  const basePrice = parseFloat(price);
  const rate = parseFloat(taxRate) || 0;
  const taxAmount = (basePrice * rate) / 100;
  const totalPrice = basePrice + taxAmount;

  return {
    basePrice: Math.round(basePrice * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    totalPrice: Math.round(totalPrice * 100) / 100,
    taxRate: rate,
  };
};

/**
 * Calculate inclusive tax (price includes tax)
 * @param {number} totalPrice - Price including tax
 * @param {number} taxRate - Tax rate in percentage
 * @returns {object} Tax calculation breakdown
 */
export const calculateInclusiveTax = (totalPrice, taxRate) => {
  if (!totalPrice || totalPrice <= 0) {
    return { basePrice: 0, taxAmount: 0, totalPrice: 0, taxRate: 0 };
  }

  const total = parseFloat(totalPrice);
  const rate = parseFloat(taxRate) || 0;
  const basePrice = total / (1 + rate / 100);
  const taxAmount = total - basePrice;

  return {
    basePrice: Math.round(basePrice * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    totalPrice: Math.round(total * 100) / 100,
    taxRate: rate,
  };
};

/**
 * Get suggested tax rate from HSN/SAC code
 * @param {string} hsnCode - HSN or SAC code
 * @returns {number} Suggested tax rate
 */
export const getTaxRateFromHSN = (hsnCode) => {
  if (!hsnCode) return GST_SLABS.HIGH; // Default to 18%
  
  const code = hsnCode.toString().trim();
  
  // Check for SAC code (services)
  if (code.startsWith('99')) {
    return GST_SLABS.HIGH;
  }
  
  // Check first 2 digits for chapter
  const chapter = code.substring(0, 2);
  if (HSN_TAX_MAP[chapter]) {
    return HSN_TAX_MAP[chapter];
  }
  
  // Check first digit
  const firstDigit = code.substring(0, 1);
  if (HSN_TAX_MAP[firstDigit]) {
    return HSN_TAX_MAP[firstDigit];
  }
  
  return GST_SLABS.HIGH; // Default
};

/**
 * Validate HSN/SAC code format
 * @param {string} code - HSN or SAC code
 * @returns {object} Validation result
 */
export const validateHSNCode = (code) => {
  if (!code) {
    return { valid: false, error: 'HSN/SAC code is required' };
  }

  const trimmedCode = code.toString().trim();
  
  // HSN codes are 4, 6, or 8 digits
  // SAC codes are 6 digits starting with 99
  const validLengths = [4, 6, 8];
  
  if (!/^\d+$/.test(trimmedCode)) {
    return { valid: false, error: 'HSN/SAC code must contain only digits' };
  }
  
  if (!validLengths.includes(trimmedCode.length)) {
    return { valid: false, error: 'HSN/SAC code must be 4, 6, or 8 digits' };
  }
  
  return { valid: true, isSAC: trimmedCode.startsWith('99') };
};

/**
 * Calculate discount percentage
 * @param {number} originalPrice - Original/Compare price
 * @param {number} salePrice - Current sale price
 * @returns {number} Discount percentage
 */
export const calculateDiscount = (originalPrice, salePrice) => {
  if (!originalPrice || !salePrice || originalPrice <= 0) {
    return 0;
  }
  
  const discount = ((originalPrice - salePrice) / originalPrice) * 100;
  return Math.round(Math.max(0, discount));
};

/**
 * Format currency (INR)
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return '₹0.00';
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export default {
  calculateTax,
  calculateInclusiveTax,
  getTaxRateFromHSN,
  validateHSNCode,
  calculateDiscount,
  formatCurrency,
  GST_SLABS,
};
